<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('works.work_approval.name')"
      :show-add="false"
      :show-filter="true"
      :show-refresh="reloadActiveGrid"
      @filter="filterPanelVisible = true"
      @columns="openActiveColumnChooser"
      @export="exportActiveGrid"
    />

    <GridFilter
      :visible.sync="filterPanelVisible"
      :title="$t('common.filter')"
      @apply="applyFilters"
      @reset="resetFilters"
    >
      <DateRangeFilter
        :dateFrom.sync="filters.date_from"
        :dateTo.sync="filters.date_to"
      />
    </GridFilter>

    <div class="content-body pt-1">
      <div id="toast-msg"></div>

      <div class="work-approval-tabs">
        <button
          :class="['work-approval-tab-btn', { active: activeTab === 'works' }]"
          @click="switchTab('works')"
        >{{ $t('works.work_approval.tab_works') }}</button>
        <button
          :class="['work-approval-tab-btn', { active: activeTab === 'productions' }]"
          @click="switchTab('productions')"
        >{{ $t('works.work_approval.tab_productions') }}</button>
      </div>

      <WorkApprovalWorksGrid
        v-show="activeTab === 'works'"
        ref="worksTabGrid"
        :filters="filters"
        :can-edit="canEdit"
      />
      <WorkApprovalProductionsGrid
        v-if="loadedTabs.productions"
        v-show="activeTab === 'productions'"
        ref="productionsTabGrid"
        :filters="filters"
        :can-edit="canEdit"
      />
    </div>
  </div>
</template>

<script>
import { addMonths } from "date-fns";
import { mapGetters } from "vuex";
import { canView, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DateRangeFilter from "@/components/DxGrid/DateRangeFilter.vue";
import WorkApprovalWorksGrid from "@/pages/works/work-approval/partials/WorkApprovalWorksGrid.vue";
import WorkApprovalProductionsGrid from "@/pages/works/work-approval/partials/WorkApprovalProductionsGrid.vue";

export default {
  name: "WorkApproval",
  middleware: ["auth", "check-company-admin"],
  components: {
    GridPanelHeader,
    GridFilter,
    DateRangeFilter,
    WorkApprovalWorksGrid,
    WorkApprovalProductionsGrid,
  },
  data() {
    const today = new Date();
    return {
      activeTab: "works",
      loadedTabs: {
        works: true,
        productions: false,
      },
      filterPanelVisible: false,
      filters: {
        date_from: addMonths(today, -11),
        date_to: addMonths(today, 1),
      },
      defaultFilters: {
        date_from: addMonths(today, -11),
        date_to: addMonths(today, 1),
      },
    };
  },
  computed: {
    ...mapGetters({
      generalSettings: "settings/generalSettings",
    }),
    config() {
      return {
        permission: permissions.WORK_APPROVAL,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this) && this.workApprovalSettingEnabled;
    },
    workApprovalSettingEnabled() {
      const setting = this.generalSettings?.find(
        (item) => (item?.code || "").toUpperCase() === "ENABLE_WORK_CONSUMPTION_AND_PRODUCTION_APPROVAL"
      );

      return [true, 1, "1", "true"].includes(setting?.value);
    },
    canEdit() {
      return canEdit(this);
    },
  },
  methods: {
    getActiveGrid() {
      return this.activeTab === "works" ? this.$refs.worksTabGrid : this.$refs.productionsTabGrid;
    },
    switchTab(tab) {
      if (this.activeTab === tab) {
        this.refreshActiveGrid();
        return;
      }
      this.loadedTabs[tab] = true;
      this.activeTab = tab;
      this.$nextTick(() => {
        this.refreshActiveGrid();
      });
    },
    applyFilters() {
      this.filterPanelVisible = false;
      this.reloadActiveGrid();
    },
    resetFilters() {
      this.filters = { ...this.defaultFilters };
      this.filterPanelVisible = false;
      this.reloadActiveGrid();
    },
    reloadActiveGrid() {
      this.getActiveGrid()?.reload?.();
    },
    refreshActiveGrid() {
      this.getActiveGrid()?.activate?.();
    },
    openActiveColumnChooser() {
      this.getActiveGrid()?.openColumnChooser?.();
    },
    exportActiveGrid() {
      this.getActiveGrid()?.exportGrid?.();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/smagriaTabs.scss";

.work-approval-tabs {
  @include smagria-tab-list;
}

.work-approval-tab-btn {
  @include smagria-tab-button;
}
</style>
